import React, { useEffect } from 'react';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { useAuth } from './contexts/AuthContext';

// Create router instance with a singleton pattern to prevent duplicates
let routerInstance: ReturnType<typeof createRouter> | null = null;

// 404 Not Found Component
const NotFoundComponent = () => {
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh',
      padding: '2rem',
      textAlign: 'center'
    }}>
      <h1 style={{ fontSize: '3rem', marginBottom: '1rem', color: 'var(--color-text-primary, #1f2937)' }}>
        404
      </h1>
      <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-text-primary, #1f2937)' }}>
        Page Not Found
      </h2>
      <p style={{ color: 'var(--color-text-secondary, #6b7280)', marginBottom: '2rem' }}>
        The page you're looking for doesn't exist or has been moved.
      </p>
      <a 
        href="/" 
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: 'var(--color-accent, #3b82f6)',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '0.5rem',
          fontWeight: '500'
        }}
      >
        Go to Home
      </a>
    </div>
  );
};

const getRouter = () => {
  if (!routerInstance) {
    routerInstance = createRouter({
      routeTree,
      defaultPreload: 'intent',
      defaultNotFoundComponent: NotFoundComponent,
      context: {
        auth: {
          isAuthenticated: false,
          user: null,
        },
      },
    });
  }
  return routerInstance;
};

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}

const PUBLIC_PATHS = ['/', '/setup', '/auth/entra-callback'];

// Component that provides router context with actual auth state
const RouterWithAuth: React.FC = () => {
  const auth = useAuth();
  const router = React.useMemo(() => getRouter(), []);
  
  useEffect(() => {
    if (auth.isLoading || auth.isAuthenticated) {
      return;
    }

    const currentPath = router.state.location.pathname;
    if (!PUBLIC_PATHS.includes(currentPath)) {
      router.navigate({ to: '/' });
    }
  }, [auth.isAuthenticated, auth.isLoading, router]);
  
  // Don't render router until auth initialization is complete
  // Only block the router during initial session bootstrap — not during login/MFA.
  if (auth.isLoading && !auth.pendingMfaChallenge) {
    return <div>Loading...</div>; // You can replace this with a proper loading component
  }
  
  return (
    <RouterProvider
      router={router}
      context={{
        auth: {
          isAuthenticated: auth.isAuthenticated,
          user: auth.user ? {
            id: auth.user.id,
            role: auth.user.role,
            username: auth.user.username,
            email: auth.user.email,
          } : null,
        },
      }}
    />
  );
};

export const AppRouter = () => {
  return <RouterWithAuth />;
};
